import { useEffect, useRef, useState } from "react";

import { useKioskSaveDiaryMutation } from "../api/klassenbuchKioskApi";
import { BookArrowIcon, CheckIcon, XIcon } from "../utils/openEntriesHelpers";

export function InhaltCell({
  lessonId,
  lessonIds,
  userId,
  hasContent,
  onOpenKlassenbuch,
}: {
  lessonId: string;
  /** Optional Doppelstunde override — when present, the inline diary
   *  edit writes the same `entryText` to every lesson_id in this list
   *  instead of only `lessonId`. Used by the kiosk's OpenEntriesList
   *  when it folds consecutive same-class/subject lessons into one row.
   *  When undefined or empty, behaviour is unchanged (single-lesson save). */
  lessonIds?: string[];
  /** The teacher's user_id (from fn_kiosk_chip_login response).
   *  Passed through to fn_klassenbuch_upsert_diary_entry so the
   *  paired-kiosk path stamps the right `created_by`. See
   *  20260518_kiosk_diary_entry_paired_callable.sql. */
  userId: string;
  hasContent: boolean;
  onOpenKlassenbuch: () => void;
}) {
  const iconButtonClass =
    "inline-flex items-center justify-center rounded p-1 cursor-pointer transition-colors hover:bg-slate-100 focus:outline-none focus:ring-1 focus:ring-slate-300";
  const [editing, setEditing] = useState(false);
  const [text, setText] = useState("");
  const [saved, setSaved] = useState(false);
  const [saveDiary] = useKioskSaveDiaryMutation();
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    if (editing) ref.current?.focus();
  }, [editing]);

  const handleBlur = async () => {
    const trimmed = text.trim();
    setEditing(false);
    if (!trimmed) return;
    // Save targets: either every lesson in a Doppelstunde group, or just
    // this single lesson (the regular case). De-dupe defensively.
    const targets = Array.from(
      new Set(
        lessonIds && lessonIds.length > 0 ? lessonIds : [lessonId],
      ),
    );
    try {
      await Promise.all(
        targets.map((id) =>
          saveDiary({
            lessonId: id,
            userId,
            entryText: trimmed,
            homework: null,
          }).unwrap(),
        ),
      );
      setSaved(true);
    } catch {
      // leave unsaved so teacher can retry quickly
    }
  };

  if (hasContent || saved) {
    return (
      <div className="flex items-center justify-center gap-2">
        <CheckIcon className="w-4 h-4 text-emerald-600" />
        <button
          onClick={onOpenKlassenbuch}
          title="Im Klassenbuch öffnen"
          className={`${iconButtonClass} text-slate-500 hover:text-slate-700`}
        >
          <BookArrowIcon />
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={(e) => { if (e.key === "Escape") { setText(""); setEditing(false); } }}
        rows={2}
        placeholder="Unterrichtsinhalt eintragen…"
        className="w-full px-2 py-1 text-sm border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-400 resize-y text-slate-800"
        onClick={(e) => e.stopPropagation()}
      />
    );
  }

  return (
    <div className="flex items-center justify-center gap-2">
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        title="Inline eintragen"
        className={iconButtonClass}
      >
        <XIcon className="w-4 h-4 text-red-600" />
      </button>
      <button
        onClick={onOpenKlassenbuch}
        title="Im Klassenbuch bearbeiten"
        className={`${iconButtonClass} text-slate-500 hover:text-slate-700`}
      >
        <BookArrowIcon />
      </button>
    </div>
  );
}
